import { useEffect, useState, useRef } from 'react';
import styled, { css } from 'styled-components';
import Input from 'common/ui/Input';
import Text from 'common/ui/Text';
import Fieldset from 'common/ui/Fieldset';
import { Stack } from 'common/ui/Layout';
import { Colors, Spacings } from 'styles';
import { ReactComponent as AutoAwesome } from 'assets/AutoAwesome.svg';
import Mic from '@mui/icons-material/Mic';
import Cancel from '@mui/icons-material/Cancel';
import StopSharp from '@mui/icons-material/StopSharp';
import { rootTheme } from 'theme/rootTheme';
import { TABLE_BG_COLOR } from './hpStyles';

const fieldLabelCss = css`
  margin-bottom: 5px !important;
`;

const fieldSubtitleCss = css`
  margin-top: 0;
  display: block;
`;

const fieldsetCss = css`
  margin-bottom: ${Spacings.xsmall}rem;
  padding: 0;
`;

const holographicInputCss = css`
  @keyframes shimmer {
    0% {
      background-position: 0% 50%;
    }
    100% {
      background-position: 200% 50%;
    }
  }

  position: relative;
  height: 32px;
  color: ${Colors.gray10} !important;
  border: 1px solid transparent !important;
  background-clip: padding-box, border-box !important;
  background-origin: padding-box, border-box !important;
  background-image:
    linear-gradient(${TABLE_BG_COLOR}, ${TABLE_BG_COLOR}),
    linear-gradient(90deg, ${Colors.gradientStart}, ${Colors.gradientEnd}, ${Colors.gradientStart}) !important;
  background-size: 200% 100% !important;
  animation: shimmer 3s linear infinite !important;
  padding-left: 12px;
  padding-right: 8px !important;
  border-radius: ${rootTheme.radius.md}px !important;
  margin: 0 !important;

  &:focus,
  &:focus-visible,
  &:focus-within {
    outline: none !important;
  }
`;

const SuffixButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.xsmall}rem;
`;

const IconButton = styled.button<{ isActive?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => (props.isActive ? Colors.blue4 : Colors.gray8)};
  transition: color 0.2s ease;

  &:hover {
    color: ${(props) => (props.isActive ? Colors.blue5 : Colors.gray10)};
  }

  &:focus {
    outline: none;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const PrefixIconContainer = styled.div`
  display: flex;
  align-items: center;
  padding-right: 4px;
`;

const GradientIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    fill: url(#magic-icon-gradient);
  }
`;

const ErrorMessage = styled(Text)`
  margin-top: 4px;
  display: block;
`;

const GradientDefs = () => (
  <svg width="0" height="0" style={{ position: 'absolute' }}>
    <defs>
      <linearGradient id="magic-icon-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={Colors.gradientStart} />
        <stop offset="100%" stopColor={Colors.gradientEnd} />
      </linearGradient>
    </defs>
  </svg>
);

interface MagicInputBaseProps {
  value: string;
  label?: string;
  subtitle?: string;
  placeholder: string;
  onValueChange: (value: string) => void;
  onBlur?: () => void;
  testId: string;
  errorMessage?: string | null;
  iconLocation?: 'label' | 'field';
}

export const MagicInputBase = ({
  value,
  label,
  subtitle,
  placeholder,
  onValueChange,
  onBlur,
  testId,
  errorMessage,
  iconLocation = 'field',
}: MagicInputBaseProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef<any>(null);
  const baseInputValueRef = useRef<string>(''); // Store the input value before recording starts

  // Initialize Web Speech API
  useEffect(() => {
    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognitionAPI =
        (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      const recognition = new SpeechRecognitionAPI();

      recognition.continuous = true;
      // interimResults is intentionally disabled to only process final speech recognition results.
      // recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event: any) => {
        // Get all transcripts from the current recording session
        const transcript = Array.from(event.results)
          .map((result: any) => result[0].transcript)
          .join('');

        // Combine the base value (before recording) with the new transcript
        const baseText = baseInputValueRef.current?.trim() ?? '';
        const newValue = baseText ? `${baseText} ${transcript}` : transcript;
        onValueChange(newValue);
      };

      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
      };

      recognition.onend = () => {
        setIsRecording(false);
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [onValueChange]);

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      console.warn('Speech recognition not supported');
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      // Store the current input value before starting recording
      baseInputValueRef.current = value ?? '';
      recognitionRef.current.start();
      setIsRecording(true);
    }
  };

  const handleClear = () => {
    onValueChange('');
  };

  return (
    <Fieldset css={fieldsetCss}>
      <GradientDefs />
      {iconLocation === 'label' && (label || subtitle) && (
        <Stack
          alignY="start"
          css={css`
            gap: ${Spacings.xsmall}rem;
            margin-left: -${Spacings.small}rem;
          `}
        >
          <GradientIcon
            css={css`
              margin-top: 4px;
            `}
          >
            <AutoAwesome />
          </GradientIcon>
          <div>
            {label && (
              <Text
                css={css`
                  ${fieldLabelCss}
                  font-size: 16px;
                  line-height: 24px;
                  color: ${Colors.gray10};
                `}
              >
                {label}
              </Text>
            )}
            {subtitle && (
              <Text css={fieldSubtitleCss} variant="caption" color="secondary-alt">
                {subtitle}
              </Text>
            )}
          </div>
        </Stack>
      )}
      {iconLocation === 'field' && (
        <>
          {label && (
            <Text
              css={css`
                ${fieldLabelCss}
                font-size: 16px;
                line-height: 24px;
                color: ${Colors.gray10};
              `}
            >
              {label}
            </Text>
          )}
          {subtitle && (
            <Text css={fieldSubtitleCss} variant="caption" color="secondary-alt">
              {subtitle}
            </Text>
          )}
        </>
      )}
      <Input
        name={testId}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        data-testid={testId}
        css={holographicInputCss}
        customPrefix={
          iconLocation === 'field' ? (
            <PrefixIconContainer>
              <GradientIcon>
                <AutoAwesome />
              </GradientIcon>
            </PrefixIconContainer>
          ) : undefined
        }
        customSuffix={
          <SuffixButtonContainer>
            <IconButton
              type="button"
              onClick={toggleRecording}
              isActive={isRecording}
              title={isRecording ? 'Stop recording' : 'Start recording'}
            >
              <GradientIcon>{isRecording ? <StopSharp /> : <Mic />}</GradientIcon>
            </IconButton>
            <IconButton type="button" onClick={handleClear} title="Clear input">
              <Cancel />
            </IconButton>
          </SuffixButtonContainer>
        }
      />
      {errorMessage && (
        <ErrorMessage data-testid="error-msg" variant="caption" color="error">
          {errorMessage}
        </ErrorMessage>
      )}
    </Fieldset>
  );
};
